import { DevShowProp } from "@/components/DevShowProp"
import { QueryRender } from "@/components/helpers/QueryRender"
import { UserSmallProfile } from "@/components/ui/UserSmallProfile"
import { useProcess } from "@/contexts/ProcessContext"
import { JustificationValidationStatus } from "@/entities/attendanceEvent/AttendanceEventType"
import {
   useAttendanceEvent,
   useDeleteAttendanceEvent,
   useUpdateAttendanceEvent,
} from "@/entities/attendanceEvent/useAttendanceEvent"
import { useUser } from "@/entities/user/useUser"
import { uploadFile } from "@/firebase/storage"
import { useAuth } from "@/hooks/useAuth"
import { useRef, useState } from "react"
import { useParams } from "react-router-dom"
import {
   icon_outline_arrow_path,
   icon_outline_bars3_bottom_left,
   icon_outline_calendar,
   icon_outline_check,
   icon_outline_document,
   icon_outline_information_circle,
   icon_outline_x_mark,
} from "@/utils/svgIcons"
import {
   formatDateRange,
   getStatusBadge,
   getTypeIcon,
   getTypeTitle,
} from "@/utils/commonAttendanceUi"
import { User } from "@/entities/user/user"
import clsx from "clsx"
import { ConfirmDialog } from "@/components/ui/confirmDialog"
import { recurrencePeriodTranslations } from "@/types/recurrence"
import { format } from "date-fns"
import { fr } from "date-fns/locale"
import { useTimeMachine } from "@/contexts/TimeMachineContext"
import { deleteRecurringAttendanceEvents } from "@/controller/recurringAttendanceEvents"
import { runTransactionBatch } from "@/firestore/transactions"

export function AttendanceEventContent({
   id,
   user,
   variant,
   onDeleted
}: {
   id: string
   user: User
   variant: "small" | "full"
   onDeleted?: () => void
}) {
   const { now } = useTimeMachine()
   const { data: attendanceEvent, ...query } = useAttendanceEvent({ id })
   const { startProcess } = useProcess()
   const updateAttendanceEvent = useUpdateAttendanceEvent()
   const deleteAttendanceEvent = useDeleteAttendanceEvent()
   const fileInputRef = useRef<HTMLInputElement>(null)
   const [adminComment, setAdminComment] = useState("")
   const userId = user?.id
   const userRole = user?.role
   const isAdmin = userRole === "admin"
   const isYoung = userRole === "young"
   const { data: young } = useUser({ id: attendanceEvent?.young_id })

   const [showConfirmDialog, setShowConfirmDialog] = useState(false)
   const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false)
   const [showConfirmCancelForFirstOccurenceDialog, setShowConfirmCancelForFirstOccurenceDialog] = useState(false)
   const [showCancelRecurringEventDialog, setShowCancelRecurringEventDialog] = useState(false)
   const pendingActionRef = useRef<{ status: JustificationValidationStatus }>()

   const handleUpload = async () => {
      const file = fileInputRef.current?.files?.[0]
      if (!file || !attendanceEvent) return

      startProcess(async () => {
         const fileUrl = await uploadFile(file, userId)
         await updateAttendanceEvent(
            attendanceEvent.id,
            {
               justification_doc: fileUrl,
               justification_validation_status: "admin_pending",
               admin_comment: "",
            },
            {
               quickMode: true,
            },
         )
      })
   }

   const handleDownload = async () => {
      if (!attendanceEvent?.justification_doc) return

      startProcess(async () => {
         window.open(attendanceEvent.justification_doc, "_blank")
      })
   }

   const handleConfirmUpdate = async () => {
      const status = pendingActionRef.current?.status
      if (!status) return

      startProcess(async () => {
         await updateAttendanceEvent(attendanceEvent!.id, {
            justification_validation_status: status,
            admin_comment: adminComment,
            justified: status === "validated",
         },{
            quickMode: true,
         })
         setShowConfirmDialog(false)
      })
   }

   const handleStatusUpdate = async (status: JustificationValidationStatus) => {
      pendingActionRef.current = { status }

      if (!adminComment && status === "rejected") {
         setShowConfirmDialog(true)
         return
      }

      await handleConfirmUpdate()
   }

   const handleStartCancel = () => {
      if (attendanceEvent?.is_recurring) {
         setShowConfirmCancelForFirstOccurenceDialog(true)
      } else {
         setShowConfirmCancelDialog(true)
      }
   }

   const handleCancel = async () => {
      startProcess(async () => {
         await deleteAttendanceEvent(attendanceEvent!.id, { quickMode: true })
         onDeleted?.()
      })
   }

   const handleCancelWithFutureEvents = async () => {
      startProcess(async () => {
         await runTransactionBatch(async () => {
            await deleteRecurringAttendanceEvents({
               recurring_group: attendanceEvent!.recurring_group!,
               minEventFrom: attendanceEvent!.from,
               deleteAttendanceEvent: (id) => deleteAttendanceEvent(id, { quickMode: true })
            })
         })
         onDeleted?.()
      })
   }

   const small = variant === "small"
   const full = variant === "full"
   const canCancel = isAdmin || (isYoung && attendanceEvent?.from && attendanceEvent.from > now)

   return (
      <>
         <QueryRender query={query} data={attendanceEvent} emptyMessage="Événement non trouvé">
            {(attendanceEvent) => (
               <>
                  <DevShowProp text={attendanceEvent.id} label="ID" />
                  <div className={clsx("flex flex-col", small && "gap-2", full && "gap-4")}>
                     <div className="flex items-center justify-between gap-2 mb-2">
                        {isAdmin && young && (
                           <UserSmallProfile
                              user={young}
                              size={full ? "lg" : "base"}
                              clickToGoToResume
                           />
                        )}
                        <div>
                           {getStatusBadge(
                              attendanceEvent.justified,
                              attendanceEvent.justification_validation_status,
                              full,
                           )}
                        </div>
                     </div>

                     <div className="flex gap-4 items-center">
                        <span className="w-6">{getTypeIcon(attendanceEvent.type)}</span>
                        <span>
                           {getTypeTitle(attendanceEvent.type)}
                           {attendanceEvent.type === "late" && (
                              <span className="ml-3 font-light">
                                 ({attendanceEvent.delayMinutes} minutes)
                              </span>
                           )}
                        </span>
                     </div>

                     <div className="flex gap-4 items-center">
                        <span className="w-6">{icon_outline_calendar}</span>
                        <span className="font-medium">
                           {formatDateRange(
                              attendanceEvent.from,
                              attendanceEvent.to,
                              attendanceEvent.type,
                           )}
                        </span>
                     </div>

                     {attendanceEvent.is_recurring && (
                        <div className="flex gap-4 items-center">
                           <span className="w-6">{icon_outline_arrow_path}</span>
                           <div className="flex flex-col">
                              <span>Se répète chaque {recurrencePeriodTranslations[attendanceEvent.recurring_period!]}</span>
                              <span>jusqu'au {format(attendanceEvent.recurring_end_date!, "d MMMM yyyy", { locale: fr })}</span>
                           </div>
                        </div>
                     )}

                     {full && attendanceEvent.reason && (
                        <div className="flex gap-4 items-center ">
                           <span className="w-6">{icon_outline_bars3_bottom_left}</span>
                           {attendanceEvent.reason}
                        </div>
                     )}

                     {full && (
                        <div className="flex gap-4 items-center ">
                           <span className="w-6">{icon_outline_document}</span>
                           {!attendanceEvent.justification_doc && (
                              <span className="text-gray-500">Aucun justificatif</span>
                           )}
                           {attendanceEvent.justification_doc && (
                              <button className="btn btn-sm  " onClick={handleDownload}>
                                 Télécharger le justificatif
                              </button>
                           )}
                        </div>
                     )}

                     {full && attendanceEvent.admin_comment && (
                        <div role="alert" className="alert  ">
                           {icon_outline_information_circle}
                           <span>{attendanceEvent.admin_comment}</span>
                        </div>
                     )}

                     {full &&
                        isYoung &&
                        ["upload_pending", "rejected"].includes(
                           attendanceEvent.justification_validation_status,
                        ) && (
                           <div className=" ">
                              <div className="label">
                                 <span className="label-text">
                                    Envoie ton{" "}
                                    {attendanceEvent.justification_validation_status ===
                                       "rejected" && "nouveau"}{" "}
                                    justificatif
                                 </span>
                              </div>
                              <div className="flex items-center gap-4">
                                 <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="file-input file-input-bordered file-input-primary w-full max-w-xs"
                                    accept="image/*,.pdf"
                                    required
                                    onChange={handleUpload}
                                 />
                              </div>
                           </div>
                        )}
                  </div>

                  {full && canCancel && (
                     <>
                        <div className="divider mt-12"></div>
                        <div className="flex justify-center">
                           <button
                              className="btn btn-secondary w-32"
                              onClick={() => handleStartCancel()}
                           >
                              {icon_outline_x_mark}
                              Annuler
                           </button>
                        </div>
                     </>
                  )}

                  {full &&
                     isAdmin &&
                     attendanceEvent.justification_validation_status === "admin_pending" && (
                        <>
                           <div className="divider mt-12">Admin</div>

                           <div className="mt-6">
                              <div className="form-control">
                                 <input
                                    type="text"
                                    placeholder="Commentaire (optionnel)"
                                    className="input input-bordered w-full  "
                                    value={adminComment}
                                    onChange={(e) => setAdminComment(e.target.value)}
                                 />
                              </div>
                              <div className="mt-4 flex gap-8 justify-center">
                                 <button
                                    className="btn btn-secondary w-32"
                                    onClick={() => handleStatusUpdate("rejected")}
                                 >
                                    {icon_outline_x_mark}
                                    Refuser
                                 </button>
                                 <button
                                    className="btn btn-primary w-32"
                                    onClick={() => handleStatusUpdate("validated")}
                                 >
                                    {icon_outline_check}
                                    Valider
                                 </button>
                              </div>
                           </div>
                        </>
                     )}
               </>
            )}
         </QueryRender>

         {showConfirmDialog && (
            <ConfirmDialog
               message="Voulez-vous vraiment refuser cette justification sans ajouter de commentaire ?"
               onConfirm={() => {
                  handleConfirmUpdate()
                  setShowConfirmDialog(false)
               }}
               onCancel={() => setShowConfirmDialog(false)}
            />
         )}
         
         {showConfirmCancelDialog && (
            <ConfirmDialog
               message="Voulez-vous vraiment annuler cet événement ?"
               onConfirm={() => {
                  handleCancel()
                  setShowConfirmCancelDialog(false)
               }}
               onCancel={() => setShowConfirmCancelDialog(false)}
            />
         )}

         {showConfirmCancelForFirstOccurenceDialog && (
            <ConfirmDialog
               message="Voulez-vous vraiment annuler cet événement ?"
               onConfirm={() => {
                  setShowConfirmCancelForFirstOccurenceDialog(false)
                  setShowCancelRecurringEventDialog(true)
               }}
               onCancel={() => setShowConfirmCancelForFirstOccurenceDialog(false)}
            />
         )}

         {showCancelRecurringEventDialog && (
            <ConfirmDialog
               message="Voulez-vous aussi annuler les événements répétés du futur ?"
               onConfirm={() => {
                  handleCancelWithFutureEvents()
                  setShowCancelRecurringEventDialog(false)
               }}
               onCancel={() => {
                  handleCancel()
                  setShowCancelRecurringEventDialog(false)
               }}
            />
         )}
      </>
   )
}

export default function AttendanceEventPage() {
   const { id } = useParams<{ id: string }>()
   const { user } = useAuth()
   if (!id || !user) return null
   return (
      <div className="page-section">
         <AttendanceEventContent id={id} user={user} variant="full" />
      </div>
   )
}
